import dayjs from "dayjs";
import type { LogItemType } from "@/types/common/logs";
import CircleLine from "@/components/common/log/circle-line";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Icon,
} from "@elaniintc/goes-design-system";
import { useIntersectionObserver } from "usehooks-ts";
import { useEffect } from "react";

import { useGetFormDetail2 } from "@/queries/formaly-request";
import PreventionLog from "./prevention-log";
import SuspensionExtensionLog from "./suspension-extension-log";
import { isSuspensionExtensionLog } from "./suspension-extension-log.utils";
interface ProcedureLogsProps {
  title: string;
  items: LogItemType[];
  emptyText?: string;
  isLoading?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  formalityId?: string;
}

export default function ProcedureLogs({
  title,
  items,
  emptyText,
  isLoading = false,
  hasNextPage = false,
  fetchNextPage,
  formalityId,
}: ProcedureLogsProps) {
  const formatDate = (date: Date) => {
    return dayjs(date).format("MMM D, YYYY | HH:mm");
  };

  const actionTranslator = (action: string, subject_type?: string) => {
    if (!action) {
      return "";
    }

    if (subject_type === "formality_request") {
      if (action === "created" || action === "formality_request creado") {
        return "creado";
      }
      if (action === "updated") {
        return "actualizado";
      }
    }

    if (subject_type === "filled_form") {
      if (action === "created") {
        return "creado";
      }
      if (action === "updated") {
        return "actualizado";
      }
    }

    if (subject_type === "revision") {
      if (action === "Revision started" || action === "revision_created") {
        return "";
      }
    }

    if (action === "created") {
      return "creado";
    }
    if (action === "updated") {
      return "actualizado";
    }
    if (action === "deleted") {
      return "eliminado";
    }

    return "";
  };

  const logTypeTranslator = (type: string) => {
    if (!type) {
      return "";
    }
    if (type === "formality_request") {
      return "Solicitud de trámite";
    }
    if (type === "filled_form") {
      return "Formulario";
    }
    if (type === "revision") {
      return "Revisión";
    }
    if (type === "correction") {
      return "Corrección";
    }
    if (type === "process") {
      return "Proceso";
    }
    if (type === "user" || type === "applicant") {
      return "Usuario";
    }
    if (type === "payment_request") {
      return "Solicitud de pago";
    }
    return type;
  };

  const FormDetail = ({
    formId,
    formalityId,
  }: {
    formId: string;
    formalityId: string;
  }) => {
    const { data, isLoading } = useGetFormDetail2(formId, formalityId);

    if (!formId || !formalityId) {
      return null;
    }

    if (isLoading) {
      return (
        <div className="flex items-center gap-2 mb-2 p-2 rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
          <Icon
            type="fas"
            name="spinner-third"
            className="text-action-quiet animate-spin text-sm"
          />
          <span className="text-sm text-body-quiet">Cargando detalles...</span>
        </div>
      );
    }

    if (!data) {
      return null;
    }

    return (
      <div className="flex items-center gap-2 mb-2 p-2 rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
        <Icon type="fas" name="bars" className="text-action-quiet text-sm" />

        <span className="text-sm">{data.name}</span>
      </div>
    );
  };

  const Description = ({ log }: { log: LogItemType }) => {
    const translatedAction = actionTranslator(log.action, log.subject_type);
    const translatedType = logTypeTranslator(log.subject_type || "");

    if (log.description === "Formality request reassigned within group") {
      return <h3 className="mb-2">Reasignación de grupo</h3>;
    }

    if (log.subject_type === "payment_request") {
      if (
        log.properties?.old?.status === "PENDING" &&
        log.properties?.attributes?.status === "PAID"
      ) {
        return <h3 className="mb-2">Pago realizado</h3>;
      }
      if (log.action === "created" || log.description === "created") {
        return <h3 className="mb-2">Solicitud de pago creada</h3>;
      }
    }

    const isSelfExplanatoryDescription = (desc: string) => {
      const selfExplanatoryKeywords = [
        "Estatus de proceso",
        "Formulario completado",
        "Formulario creado",
        "Formulario en progreso",
        "Formulario actualizado",
        "Revisión",
        "Revision started",
        "Corrección solicitada",
        "Corrección",
      ];
      return selfExplanatoryKeywords.some((keyword) => desc.includes(keyword));
    };

    if (log.description && isSelfExplanatoryDescription(log.description)) {
      return <h3 className="mb-2">{log.description}</h3>;
    }

    if (log.description && translatedAction) {
      return (
        <h3 className="mb-2">
          {translatedType && `${translatedType} `}
          {translatedAction}
        </h3>
      );
    }

    if (log.description) {
      return <h3 className="mb-2">{log.description}</h3>;
    }
    return (
      <h3 className="mb-2">
        {translatedType || "Actividad"} {translatedAction || "registrada"}
      </h3>
    );
  };

  const statusTraducer = (status: string) => {
    const statusMap: Record<string, string> = {
      submitted: "Enviado",
      completed: "Completado",
      in_progress: "En progreso",
      correction: "Revisión",
      fill_correction: "Observado",
      assigned: "Pendiente",
      failed_correction: "Rechazado",
      on_construction: "En construcción",
      approved: "Aprobado",
      published: "Publicado",
      active: "Activo",
      inactive: "Archivado",
      draft: "Borrador",
      in_process: "En proceso",
      force_completed: "Completado forzosamente",
      pending: "Pendiente",
      paid: "Pagado",
    };

    return statusMap[status.toLowerCase()] || status;
  };

  const statusColor = (
    status: string,
  ): "info" | "success" | "warning" | "neutral" | "danger" => {
    const lowerStatus = status.toLowerCase();

    const colorMap: Record<
      string,
      "info" | "success" | "warning" | "neutral" | "danger"
    > = {
      submitted: "info",
      completed: "success",
      in_progress: "info",
      correction: "info",
      fill_correction: "warning",
      assigned: "neutral",
      failed_correction: "danger",
      on_construction: "info",
      approved: "success",
      published: "success",
      active: "success",
      inactive: "warning",
      draft: "neutral",
      in_process: "info",
      force_completed: "success",
      pending: "warning",
      paid: "success",
    };

    return colorMap[lowerStatus] || "neutral";
  };

  const [ref, isIntercepting, entry] = useIntersectionObserver({});

  useEffect(() => {
    if (isIntercepting && !isLoading && entry?.isIntersecting && hasNextPage) {
      fetchNextPage?.();
    }
  }, [isIntercepting, hasNextPage, entry, isLoading, fetchNextPage]);

  return (
    <div className="flex flex-col w-full h-full leading-7 text-body font-normal text-base">
      {items.length > 0 && <h2 className="mb-6">{title}</h2>}
      {/* Timeline Container */}
      {items.length !== 0 && (
        <div className="relative pb-6">
          {items.map((log, index) => {
            const isLast = index === items.length - 1;
            return (
              <div
                key={`log-${log.id}-${index}`}
                className={cn("flex gap-3 -mt-2", !isLast && "mb-0")}
              >
                <CircleLine
                  option={index === 0 ? "init" : isLast ? "end" : "middle"}
                  showLine={!isLast}
                />

                {log?.revisions && log.revisions?.length ? (
                  <div className={cn("flex-1", !isLast && "pb-6")}>
                    <PreventionLog log={log} />
                  </div>
                ) : isSuspensionExtensionLog(log) ? (
                  <SuspensionExtensionLog log={log} />
                ) : (
                  <div className={cn("flex-1", !isLast && "pb-6")}>
                    {/* Descripción */}
                    <Description log={log} />

                    {/* Detalles de formulario */}
                    {log.subject_type === "filled_form" &&
                      (log.subject?.form_id ||
                        log.properties?.attributes?.form_id) &&
                      formalityId && (
                        <FormDetail
                          formId={
                            log.subject?.form_id ||
                            log.properties?.attributes?.form_id ||
                            ""
                          }
                          formalityId={formalityId}
                        />
                      )}

                    {/* Reasignación de grupo */}
                    {log.description ===
                      "Formality request reassigned within group" &&
                      log.properties?.previous_assigned_to_user?.full_name &&
                      log.properties?.new_assigned_to_user?.full_name && (
                        <div className="flex items-center min-h-11 mb-2 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
                          <span className="text-sm text-body">De</span>
                          <span className="text-sm text-body line-through">
                            {log.properties.previous_assigned_to_user.full_name}
                          </span>
                          <Icon
                            type="fas"
                            name="arrow-right"
                            className="text-body-quiet text-sm"
                          />
                          <span className="text-sm text-body">
                            {log.properties.new_assigned_to_user.full_name}
                          </span>
                        </div>
                      )}
                    {/* Cambio de estatus en filled_form */}
                    {log.subject_type === "filled_form" &&
                      log.properties?.old?.status &&
                      log.properties?.attributes?.status &&
                      log.properties.old.status !==
                        log.properties.attributes.status && (
                        <div className="flex items-center min-h-11 mb-2 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
                          <span className="text-sm text-body">De</span>
                          <Badge
                            size="xs"
                            variant={statusColor(log.properties.old.status)}
                          >
                            {statusTraducer(log.properties.old.status)}
                          </Badge>
                          <span className="text-sm text-body">a</span>
                          <Badge
                            size="xs"
                            variant={statusColor(
                              log.properties.attributes.status,
                            )}
                          >
                            {statusTraducer(log.properties.attributes.status)}
                          </Badge>
                        </div>
                      )}

                    {/* Comentario de corrección */}
                    {log.subject_type === "correction" &&
                      log.properties?.context?.reason && (
                        <div className="flex flex-col gap-1 mb-2 p-2 rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
                          <div className="flex items-center gap-2">
                            <Icon
                              type="fas"
                              name="comment"
                              className="text-warning-quiet text-sm"
                            />
                            <span className="text-sm font-semibold text-warning-quiet">
                              Comentario:
                            </span>
                          </div>
                          <span className="text-sm text-body">
                            {log.properties.context.reason}
                          </span>
                        </div>
                      )}

                    {/* Info de pago */}
                    {log.subject_type === "payment_request" && (
                      <div className="flex flex-col gap-2 mb-2">
                        {/* Mostrar monto */}
                        {log.properties?.attributes?.amount && (
                          <div className="flex items-center min-h-11 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
                            <Icon
                              type="fas"
                              name="dollar-sign"
                              className="text-action-quiet text-sm"
                            />
                            <span className="text-sm text-body">Monto:</span>
                            <span className="text-sm font-semibold text-body">
                              $
                              {parseFloat(
                                log.properties.attributes.amount,
                              ).toFixed(2) || "0.00"}
                            </span>
                          </div>
                        )}

                        {/* Cambio de estado */}
                        {log.properties?.old?.status &&
                          log.properties?.attributes?.status &&
                          log.properties.old.status !==
                            log.properties.attributes.status && (
                            <div className="flex items-center min-h-11 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
                              <span className="text-sm text-body">De</span>
                              <Badge
                                size="xs"
                                variant={statusColor(log.properties.old.status)}
                              >
                                {statusTraducer(log.properties.old.status)}
                              </Badge>
                              <span className="text-sm text-body">a</span>
                              <Badge
                                size="xs"
                                variant={statusColor(
                                  log.properties.attributes.status,
                                )}
                              >
                                {statusTraducer(
                                  log.properties.attributes.status,
                                )}
                              </Badge>
                            </div>
                          )}

                        {/* Método de pago */}
                        {log.properties?.attributes?.payment_method && (
                          <div className="flex items-center min-h-11 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
                            <span className="text-sm text-body">
                              Método de pago:
                            </span>
                            <Badge size="xs" variant="info">
                              {log.properties.attributes.payment_method ===
                              "CREDIT_CARD"
                                ? "Tarjeta de crédito"
                                : log.properties.attributes.payment_method}
                            </Badge>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Autor */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-body">Por</span>
                      {log.author !== "Sistema" && (
                        <Avatar className="shrink-0" size="xs">
                          <AvatarFallback color="action">
                            {log.author.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <span className="text-action">
                        {log.author === "Sistema" ? "Sistema SDT" : log.author}
                      </span>
                    </div>

                    {/* Fecha */}
                    <div className="text-sm text-body-quiet capitalize">
                      {formatDate(log.date)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {hasNextPage && (
            <div
              className="w-full flex items-center justify-center h-1"
              ref={ref}
            />
          )}
        </div>
      )}
      {items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="size-12 rounded-full bg-neutral-object-accent-silent flex items-center justify-center mb-3">
            <Icon
              type="far"
              name="clipboard-list"
              className="text-body-quiet text-lg"
            />
          </div>
          <p className="text-body-loud font-semibold text-size-body-sm">
            {emptyText || "Sin actividad registrada"}
          </p>
          <p className="text-body-quiet text-size-body-xs text-center mt-1">
            No hay registros de actividad para este trámite
          </p>
        </div>
      )}
    </div>
  );
}
